import React, { useState, createContext, useContext } from "react";
import { createTheme } from "@mui/material/styles";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline, PaletteMode, useMediaQuery } from "@mui/material";

type ThemePreference = "dark" | "light" | "auto";
const ReactThemeContext = createContext<
  | {
      theme: ThemePreference;
      setTheme: (selection: ThemePreference) => void;
    }
  | undefined
>(undefined);

export const SEIColors = {
  primary: {
    blue: "#00558D",
    black: "#231F20",
  },
  secondary: {
    green: "#4C721C",
    brown: "#A68562",
    teal: "#027D69",
  },
  accents: {
    yellow: "#FFC222",
    green: "#AEBC21",
    orange: "#F26531",
  },
};

export const useThemeContext = () => {
  const context = useContext(ReactThemeContext);
  if (context === undefined) {
    throw new Error("useThemeContext must be used within a ReactThemeProvider");
  }
  return context;
};

const isValidTheme = (theme) => {
  return ["dark", "light"].includes(theme);
};

export const ADPThemeProvider = ({ children }) => {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)", {
    noSsr: true,
  });

  const [currentTheme, setCurrentTheme] = useState(() => {
    if (prefersDarkMode) {
      return "dark";
    }
    return "light";
  });

  function setTheme(selection: ThemePreference) {
    if (isValidTheme(selection)) {
      setCurrentTheme(selection);
    } else {
      const theme = prefersDarkMode ? "dark" : "light";
      setCurrentTheme(theme);
    }
  }

  const theme = createTheme({
    typography: {
      fontFamily: "'Roboto', Arial, Helvetica, sans-serif",
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightBold: 700,
      fontSize: 11,
      caption: {
        fontSize: 14,
        fontWeight: 400,
      },
      h1: {
        fontSize: 64,
        fontWeight: 400,
      },
      h2: {
        fontSize: 44,
        fontWeight: 400,
      },
      h3: {
        fontSize: 32,
        fontWeight: 400,
      },
      h4: {
        fontSize: 24,
      },
      h5: {
        fontSize: 20,
        fontWeight: 500,
      },
      h6: {
        fontSize: 14,
      },
      body1: {
        fontSize: 16,
      },
      body2: {
        fontSize: 12,
      },
      button: {
        fontSize: 14,
        textTransform: "capitalize",
      },
      subtitle1: {
        fontSize: 12,
      },
    },
    palette: {
      mode: (currentTheme === "auto" ? "light" : currentTheme) as PaletteMode,
      primary: {
        main: SEIColors.primary.blue,
        light: SEIColors.primary.blue,
        dark: "rgb(var(--arc-font-color))",
        contrastText: "#ffffff",
      },
      secondary: {
        main: SEIColors.secondary.green,
        light: SEIColors.secondary.green,
        dark: "#fff",
        contrastText: "#ffffff",
      },
      error: {
        main: SEIColors.accents.orange,
        light: SEIColors.accents.orange,
        dark: SEIColors.accents.orange,
        contrastText: "#ffffff",
      },
      warning: {
        main: SEIColors.accents.orange,
        light: SEIColors.accents.orange,
        dark: SEIColors.accents.orange,
        contrastText: "#ffffff",
      },
      info: {
        main: SEIColors.accents.green,
        light: SEIColors.accents.green,
        dark: SEIColors.accents.green,
        contrastText: "#ffffff",
      },
      success: {
        main: SEIColors.accents.green,
        light: SEIColors.accents.green,
        dark: SEIColors.accents.green,
        contrastText: "#ffffff",
      },
      background: {
        default: currentTheme === "light" ? "#fff" : "rgb(219, 219, 219",
        paper: currentTheme === "light" ? "fff" : "rgb(219, 219, 219",
      },
    },
  });
  return (
    <ReactThemeContext.Provider
      value={{
        theme: currentTheme as PaletteMode | "auto",
        setTheme,
      }}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ReactThemeContext.Provider>
  );
};
